import type {
  BuildSessionReportInput,
  BuildSessionReportOutput,
  SessionReport,
  SessionReportFinding,
  SessionReportSession,
} from "./types";
import { deriveSessionReportDashboard } from "./dashboard";
import { deriveSessionReportFindings } from "./findings";
import { deriveSessionReportHealth } from "./health";
import { deriveSessionReportSummary } from "./summary";
import { deriveSessionReportTimeline } from "./timeline";

const toIso = (timestamp: number): string => new Date(timestamp).toISOString();

const getSessionSection = (input: BuildSessionReportInput): SessionReportSession => {
  const startedAt = input.auditState.startedAt;
  const stoppedAt = input.auditState.stoppedAt;
  return {
    sessionId: input.auditState.sessionId,
    label: input.auditState.label ?? null,
    requestedProvider: input.auditState.requestedProvider,
    resolvedProvider: input.auditState.resolvedProvider,
    createdAt: toIso(input.auditState.createdAt),
    startedAt: startedAt === null ? null : toIso(startedAt),
    stoppedAt: stoppedAt === null ? null : toIso(stoppedAt),
    durationMs:
      startedAt === null ? null : Math.max((stoppedAt ?? input.now) - startedAt, 0),
    hasStarted: startedAt !== null,
    hasStopped: stoppedAt !== null,
  };
};

export const buildSessionReport = (
  input: BuildSessionReportInput
): BuildSessionReportOutput => {
  const summary = deriveSessionReportSummary(input);
  const health = deriveSessionReportHealth(input, summary);
  const timeline = deriveSessionReportTimeline(input);
  const findings: SessionReportFinding[] = deriveSessionReportFindings(input, summary);
  const dashboard = deriveSessionReportDashboard(input, summary, health);
  const report: SessionReport = {
    schemaVersion: 2,
    generatedAt: toIso(input.now),
    session: getSessionSection(input),
    summary,
    health,
    timeline,
    findings,
    dashboard,
  };

  return {
    report,
  };
};
